<template>
  <avs-card class="voucher-detail-container">
    <!-- ===== HEADER / TOOLBAR ===== -->
    <template v-slot:title>
      <!-- Desktop -->
      <div v-if="$vuetify.breakpoint.mdAndUp">
        <v-container fluid style="padding: 0;">
          <v-row dense>
            <!-- Search -->
            <v-col cols="12" md="4">
              <v-text-field
                v-model="search"
                @input="onSearchChange"
                clearable
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-magnify"
                :label="$t('commons.search')"
                class="search-field"
              />
            </v-col>

            <!-- Date début -->
            <v-col cols="12" md="3">
              <v-menu
                v-model="menuStartDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="230px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    :label="$t('Date de début')"
                    prepend-inner-icon="mdi-calendar-start"
                    outlined
                    dense
                    hide-details
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="startDate"
                  @input="applyFilters"
                  color="primary"
                />
              </v-menu>
            </v-col>

            <!-- Date fin -->
            <v-col cols="12" md="3">
              <v-menu
                v-model="menuEndDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="230px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDate"
                    :label="$t('Date de fin')"
                    prepend-inner-icon="mdi-calendar-end"
                    outlined
                    dense
                    hide-details
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="endDate"
                  @input="applyFilters"
                  color="primary"
                />
              </v-menu>
            </v-col>

            <!-- Expirés + Export -->
            <v-col cols="12" md="2" class="d-flex align-center justify-end">
              <v-switch
                v-model="showExpired"
                :label="$t('Bons expirés')"
                @change="applyFilters"
                inset
                hide-details
                class="mt-0 mr-2"
              />
              <slot name="actions" v-bind:dataset="dataset" />
            </v-col>

            <!-- Société émettrice -->
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="selectedCompany"
                :items="companies"
                item-text="name"
                item-value="pk"
                :label="$t('Client émetteur')"
                @input="applyFilters"
                clearable
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-domain"
              />
            </v-col>

            <!-- Station -->
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="selectedShop"
                clearable
                :items="shops"
                @input="applyFilters"
                item-text="name"
                item-value="id"
                :label="$t('Stations')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-map-marker"
              />
            </v-col>

            <!-- Produit -->
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="selectedProduct"
                clearable
                :items="products"
                @input="applyFilters"
                item-text="name"
                item-value="id"
                :label="$t('Produits')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-gas-station"
              />
            </v-col>

          </v-row>
        </v-container>
      </div>

      <!-- Mobile -->
      <div v-else class="col-12 mobile-header">
        <div class="mobile-page-header px-4 pt-4 pb-3">
          <div class="d-flex align-center mb-3">
            <v-icon large color="primary" class="mr-2">
              mdi-ticket-percent
            </v-icon>
            <div>
              <h3 class="text-h6 font-weight-bold mb-0">
                {{ $t("Bons d'achat") }}
              </h3>
              <small class="grey--text text--darken-1">
                {{ $t("Clients") }}
              </small>
            </div>
          </div>
        </div>

        <div class="mobile-filters px-4">
          <v-text-field
            v-model="search"
            @input="onSearchChange"
            clearable
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-magnify"
            :label="$t('commons.search')"
            class="mb-3 search-field-mobile"
          />

          <v-menu
            v-model="menuStartDate"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="startDate"
                :label="$t('Date de début')"
                prepend-inner-icon="mdi-calendar-start"
                outlined
                dense
                hide-details
                readonly
                v-bind="attrs"
                v-on="on"
                class="mb-3"
              />
            </template>
            <v-date-picker
              v-model="startDate"
              @input="applyFilters"
              color="primary"
            />
          </v-menu>

          <v-menu
            v-model="menuEndDate"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="endDate"
                :label="$t('Date de fin')"
                prepend-inner-icon="mdi-calendar-end"
                outlined
                dense
                hide-details
                readonly
                v-bind="attrs"
                v-on="on"
                class="mb-3"
              />
            </template>
            <v-date-picker
              v-model="endDate"
              @input="applyFilters"
              color="primary"
            />
          </v-menu>

          <div class="d-flex align-center mb-3">
            <v-switch
              v-model="showExpired"
              :label="$t('Bons expirés')"
              @change="applyFilters"
              inset
              hide-details
              class="mt-0 mr-2"
            />
            <slot name="actions" v-bind:dataset="dataset" />
          </div>

          <v-autocomplete
            v-model="selectedCompany"
            :items="companies"
            item-text="name"
            item-value="pk"
            :label="$t('Client émetteur')"
            @input="applyFilters"
            clearable
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-domain"
            class="mb-3"
          />

          <v-autocomplete
            v-model="selectedShop"
            clearable
            :items="shops"
            @input="applyFilters"
            item-text="name"
            item-value="id"
            :label="$t('Stations')"
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-map-marker"
            class="mb-3"
          />

          <v-autocomplete
            v-model="selectedProduct"
            clearable
            :items="products"
            @input="applyFilters"
            item-text="name"
            item-value="id"
            :label="$t('Produits')"
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-gas-station"
            class="mb-3"
          />

        </div>
      </div>
    </template>

    <!-- ===== CONTENU ===== -->
    <template v-slot:content>
      <!-- Desktop table -->
      <div v-if="$vuetify.breakpoint.mdAndUp" class="col-12 mt-0">
        <v-data-table
          class="ma-6 bgTable"
          dense
          :headers="headers"
          :items="dataset"
          :loading="loading"
          :no-data-text="$t('messages.information.no_data')"
          :no-results-text="$t('Aucune donnée disponible')"
          :loading-text="$t('Chargement des données')"
          disable-pagination
          hide-default-footer
          item-key="id"
        >
          <!-- Entreprise -->
          <template v-slot:item.company_name="{ item }">
            {{ item.vehicle_detail?.company_detail?.name || "—" }}
          </template>

          <!-- Conducteur + matricule -->
          <template v-slot:item.driver="{ item }">
            <div>{{ getDriverName(item) }}</div>
            <div class="caption grey--text">
              {{ item.vehicle_detail?.registration_number || "—" }}
            </div>
          </template>

          <template v-slot:item.purchase_amount="{ item }">
            {{ formatNumber(item.purchase_amount) }}
          </template>
          <template v-slot:item.purchase_liters="{ item }">
            {{ formatNumber(item.purchase_liters) }}
          </template>
          <template v-slot:item.remaining_amount="{ item }">
            {{ formatNumber(item.remaining_amount) }}
          </template>
          <template v-slot:item.liter_remaining="{ item }">
            {{ formatNumber(item.liter_remaining) }}
          </template>
          <template v-slot:item.valid_until="{ item }">
            {{ toDatetime(item.valid_until) }}
          </template>
          <template v-slot:item.company_shop_name="{ item }">
            {{ locationName(item.company_shop) }}
          </template>
          <template v-slot:item.company_product_name="{ item }">
            {{ item.company_product?.name || "N/A" }}
          </template>
        </v-data-table>

        <v-row
          justify="center"
          class="my-4"
          v-if="isFetchingMore && !isLastPage"
        >
          <v-progress-circular indeterminate color="primary" />
        </v-row>
      </div>

      <!-- Mobile cards -->
      <div v-else class="col-12 px-4">
        <v-skeleton-loader
          v-if="loading && !dataset.length"
          type="image, article, actions"
          class="mb-3"
        />
        <v-alert
          v-else-if="!dataset.length"
          type="info"
          dense
          outlined
          class="mb-3"
        >
          {{ $t("messages.information.no_data") }}
        </v-alert>

        <v-card
          v-for="it in dataset"
          :key="it.id"
          class="mb-3 voucher-card"
          outlined
        >
          <v-card-title class="py-3 d-flex align-center">
            <div class="voucher-title">
              <div class="veh text-truncate">
                {{ it.vehicle_detail?.registration_number || "—" }}
              </div>
              <div class="driver grey--text text--darken-1">
                {{ getDriverName(it) }}
              </div>
              <div class="company grey--text text--darken-1">
                {{ it.vehicle_detail?.company_detail?.name || "—" }}
              </div>
            </div>
          </v-card-title>

          <v-divider />

          <v-card-text class="py-3">
            <div class="grid-2">
              <div class="cell">
                <div class="label">{{ $t("Montant (XOF)") }}</div>
                <div class="value">
                  {{ formatNumber(it.purchase_amount) }}
                </div>
              </div>
              <div class="cell">
                <div class="label">{{ $tc("Litre") }}</div>
                <div class="value">
                  {{ formatNumber(it.purchase_liters) }}
                </div>
              </div>
              <div class="cell">
                <div class="label">{{ $t("Restant (XOF)") }}</div>
                <div class="value">
                  {{ formatNumber(it.remaining_amount) }}
                </div>
              </div>
              <div class="cell">
                <div class="label">{{ $t("Restant (L)") }}</div>
                <div class="value">
                  {{ formatNumber(it.liter_remaining) }}
                </div>
              </div>
              <div class="cell">
                <div class="label">{{ $t("Date de fin") }}</div>
                <div class="value">
                  {{ toDatetime(it.valid_until) }}
                </div>
              </div>
              <div class="cell">
                <div class="label">{{ $t("Site") }}</div>
                <div class="value">
                  {{ locationName(it.company_shop) }}
                </div>
              </div>
              <div class="cell">
                <div class="label">{{ $t("Produit") }}</div>
                <div class="value">
                  {{ it.company_product?.name || "N/A" }}
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <div ref="infiniteScrollTrigger" style="height: 1px;"></div>
        <v-row
          justify="center"
          class="my-4"
          v-if="isFetchingMore && !isLastPage"
        >
          <v-progress-circular indeterminate color="primary" />
        </v-row>
      </div>
    </template>
  </avs-card>
</template>

<script>
import { debounce } from "lodash";
import CompanyVouchersService from "@/services/CompanyVouchersService";
import {
  ShopDetailService,
  CompanyService,
  ProductDetailService
} from "@/services";
import { toDate, toDatetime } from "@/utils/datetime";

export default {
  name: "VouchersClientsTable",
  props: { inspectionId: { type: Number, default: null } },
  data() {
    return {
      // paging
      page: 1,
      isLastPage: false,
      isFetchingMore: false,
      loading: false,

      // dataset
      dataset: [],
      search: "",

      // filters
      selectedShop: null,
      selectedProduct: null,
      selectedCompany: null,
      showExpired: false,
      startDate: "",
      endDate: "",
      menuStartDate: false,
      menuEndDate: false,

      // refs
      shops: [],
      products: [],
      companies: [],

      headers: [
        { text: this.$t("Entreprise"), value: "company_name", width: "200" },
        { text: this.$t("Conducteur"), value: "driver", width: "190" },
        { text: this.$t("Montant (XOF)"), value: "purchase_amount", sortable: false },
        {
          text: this.$tc("Litre"),
          value: "purchase_liters",
          align: "center", width: "75"
        },
        { text: this.$t("Reste"), value: "remaining_amount", width: "100" },
        { text: this.$t("(L)"), value: "liter_remaining", width: "70" },
        { text: this.$t("Date de fin"), value: "valid_until" },
        { text: this.$t("Site"), value: "company_shop_name" },
        { text: this.$t("Produit"), value: "company_product_name" }
      ],

      getVoucherDebounced: null
    };
  },
  methods: {
    toDatetime,
    toDate,

    getDriverName(item) {
      const v = item.vehicle_detail;
      if (!v) return "N/A";
      const first = v.driver_first_name || "";
      const last = v.driver_last_name || "";
      return (first + " " + last).trim() || "N/A";
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === "") return "";
      const n = Number(value);
      if (Number.isNaN(n)) return String(value);
      const [i, d] = n.toFixed(2).split(".");
      const iFmt = i.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
      return d !== "00" ? `${iFmt},${d}` : iFmt;
    },

    locationName(v) {
      return v ? v.name || "—" : this.$t("Tout le réseau");
    },

    onSearchChange() {
      this.getVoucherDebounced();
    },

    getQueryParams(extra = {}) {
      return {
        search: this.search || undefined,
        start_date: this.startDate || undefined,
        end_date: this.endDate || undefined,
        shop: this.selectedShop || undefined,
        product: this.selectedProduct || undefined,
        company: this.selectedCompany || undefined,
        show_expired: this.showExpired || undefined,
        page: this.page,
        ...extra
      };
    },

    async getVoucher() {
      this.loading = true;
      this.page = 1;
      this.dataset = [];
      this.isLastPage = false;
      try {
        const r = await CompanyVouchersService.getClients(
          this,
          this.getQueryParams()
        );
        const results = r?.results || r || [];
        this.dataset = results;
        this.page = 2;
      } catch (err) {
        if (err?.response?.status === 403) {
          this.$store.commit("app/message", {
            msg: this.$t("messages.error.permission_denied"),
            color: "error"
          });
        }
      } finally {
        this.loading = false;
      }
    },

    async loadMoreVouchers() {
      if (this.isFetchingMore || this.isLastPage) return;
      this.isFetchingMore = true;
      try {
        const res = await CompanyVouchersService.getClients(
          this,
          this.getQueryParams({ page: this.page })
        );
        const results = res?.results || [];
        if (!results.length) {
          this.isLastPage = true;
        } else {
          const seen = new Set(this.dataset.map(i => i.id));
          const add = results.filter(i => !seen.has(i.id));
          this.dataset.push(...add);
          this.page += 1;
        }
      } catch (e) {
        if (e?.response?.status === 404) this.isLastPage = true;
        console.error("Erreur chargement bons clients :", e);
      } finally {
        this.isFetchingMore = false;
        this.$nextTick(() => { if (!this.isLastPage) this.handleScroll() });
      }
    },

    applyFilters() {
      this.getVoucher();
      this.$nextTick(() => this.loadMoreVouchers());
    },

    handleScroll() {
      const scrollBottom = window.innerHeight + window.scrollY;
      const pageBottom = document.body.offsetHeight - 300;
      if (scrollBottom >= pageBottom) this.loadMoreVouchers();
    },

    fetchShopTypes() {
      ShopDetailService.getAll(this).then(response => {
        const apiShops = response?.results || response || [];
        this.shops = [
          { id: null, name: this.$t("Tout le réseau") },
          ...apiShops
        ];
      });
    },
    fetchProductTypes() {
      ProductDetailService.getprincipalAll().then(r => {
        this.products = r?.results || r || [];
      });
    },
    fetchCompanyTypes() {
      CompanyService.getAllCompanies().then(response => {
        this.companies = response || [];
      });
    }
  },

  created() {
    this.fetchShopTypes();
    this.fetchProductTypes();
    this.fetchCompanyTypes();
  },

  mounted() {
    this.getVoucherDebounced = debounce(() => {
      this.getVoucher();
      this.$nextTick(() => this.loadMoreVouchers());
    }, 500);

    this.getVoucher();
    this.$nextTick(() => this.loadMoreVouchers());
    window.addEventListener("scroll", this.handleScroll);
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  }
};
</script>

<style scoped>
.voucher-detail-container {
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
}

/* Champs de recherche */
.search-field,
.search-field-mobile {
  border-radius: 12px;
}

/* Cartes mobiles */
.voucher-card {
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(16, 24, 40, 0.06);
}

.voucher-title {
  min-width: 0;
}

.voucher-title .veh {
  font-weight: 700;
  font-size: 16px;
  max-width: 60vw;
}

.voucher-title .driver,
.voucher-title .company {
  font-size: 12px;
  margin-top: 2px;
}

/* Grille d'infos (mobile) */
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px 16px;
}

.cell .label {
  font-size: 12px;
  color: #6b7280;
}

.cell .value {
  font-size: 14px;
  font-weight: 600;
}

@media (max-width: 360px) {
  .grid-2 {
    grid-template-columns: 1fr;
  }
}
</style>
